import { Suspense, lazy, createElement, useCallback, useEffect, useMemo, useRef } from "react";
import { renderToStaticMarkup } from "react-dom/server";
import { Loader2, Maximize2 } from "lucide-react";
import { iconForGraphNode } from "./NodeKindIcon";

// `three-spritetext` is the canonical companion for react-force-graph-3d
// — billboarded text sprites that always face the camera. Both lazy-
// loaded together (Promise.all inside `LazyForceGraph`) so by the time
// the 3D viewer mounts the SpriteText constructor is guaranteed ready.
let SpriteTextCtor: (new (text?: string) => unknown) | null = null;
// THREE is needed for per-kind 3D geometries — different shape per
// NodeKind so the scene reads as a real typed graph, not a uniform
// sphere field. Imported alongside force-graph in the same async chunk.
// eslint-disable-next-line @typescript-eslint/consistent-type-imports
type ThreeNS = typeof import("three");
let THREE_REF: ThreeNS | null = null;

/* SOTA 3D graph viz — react-force-graph-3d (three.js under the hood).
 * Used by Spotify · Apple internal · MIT. ~4.5k★, mature, GPU-fast.
 *
 * Bundle hygiene: react-force-graph-3d pulls ~600 KB of three.js
 * geometry/material code. We code-split it behind a lazy import so the
 * 2D path (the default) never pays that cost.
 *
 * Visual language: per-kind sphere colour (Apple palette) + emissive
 * glow on hover + bright tube edges with animated particle trails so
 * causality reads as flowing energy, not a static diagram.
 */

const LazyForceGraph = lazy(async () => {
  // Load force-graph + three-spritetext + three together so when
  // nodeThreeObject is first called, all three constructors are ready.
  const [mod, spriteMod, threeMod] = await Promise.all([
    import("react-force-graph-3d"),
    import("three-spritetext"),
    import("three"),
  ]);
  SpriteTextCtor = (spriteMod.default as unknown) as new (text?: string) => unknown;
  THREE_REF = threeMod as ThreeNS;
  return { default: mod.default };
});

/* ─── Badge texture caches ────────────────────────────────────────
 * three.js's TextureLoader races with WebGL upload — the renderer
 * sees a 0×0 image, allocates immutable storage, then can't upload
 * the real pixels ("texSubImage2D: bad image data"). Fix: render the
 * SVG through HTMLImageElement → onload → 2D canvas → CanvasTexture.
 * CanvasTextures are always valid synchronously.
 *
 * Cached at module scope so identical (color + icon) badges share
 * one texture across all nodes AND across project switches.
 */
const BADGE_TEXTURE_CACHE = new Map<string, import("three").CanvasTexture>();
const BADGE_LOADING = new Map<string, Promise<import("three").CanvasTexture>>();

function badgeKey(fillHex: string, iconKey: string): string {
  return `${fillHex}::${iconKey}`;
}

function loadBadgeTexture(
  THREE: ThreeNS,
  fillHex: string,
  iconKey: string,
  iconSvg: string,
): Promise<import("three").CanvasTexture> {
  const key = badgeKey(fillHex, iconKey);
  const cached = BADGE_TEXTURE_CACHE.get(key);
  if (cached) return Promise.resolve(cached);
  const inFlight = BADGE_LOADING.get(key);
  if (inFlight) return inFlight;

  const promise = new Promise<import("three").CanvasTexture>((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      const SIZE = 256;
      const canvas = document.createElement("canvas");
      canvas.width = canvas.height = SIZE;
      const ctx = canvas.getContext("2d");
      if (!ctx) { reject(new Error("no 2d ctx")); return; }
      // Filled coloured disk
      ctx.fillStyle = fillHex;
      ctx.beginPath();
      ctx.arc(SIZE / 2, SIZE / 2, SIZE / 2 - 6, 0, Math.PI * 2);
      ctx.fill();
      // Soft outline ring
      ctx.strokeStyle = "rgba(0,0,0,0.18)";
      ctx.lineWidth = 3;
      ctx.stroke();
      // Tabler icon inset — drawn over the disk, centred. 72% of the
      // disk so the glyph reads clearly even at full-scene zoom.
      const ICON_SIZE = SIZE * 0.72;
      const off = (SIZE - ICON_SIZE) / 2;
      ctx.drawImage(img, off, off, ICON_SIZE, ICON_SIZE);
      const tex = new THREE.CanvasTexture(canvas);
      tex.minFilter = THREE.LinearFilter;
      tex.anisotropy = 8;
      BADGE_TEXTURE_CACHE.set(key, tex);
      BADGE_LOADING.delete(key);
      resolve(tex);
    };
    img.onerror = () => {
      BADGE_LOADING.delete(key);
      reject(new Error("svg image decode failed"));
    };
    img.src = `data:image/svg+xml;utf8,${encodeURIComponent(iconSvg)}`;
  });
  BADGE_LOADING.set(key, promise);
  return promise;
}

export interface Graph3DNode {
  id: string;
  label: string;
  kind: string;          // free-form — drives the colour palette below
  description?: string | undefined;
  group?: string | undefined;
  brand_color?: string | undefined;  // override per-node (e.g. official brand hex)
  /** Typed subtype — for SKU it's `attrs.category`, for Room it's
   *  `attrs.roomKind`, for Zone it's `attrs.zoneKind`. Single field
   *  on the wire, dispatched per-kind on the receiving side. */
  category?: string | undefined;
}

export interface Graph3DLink {
  source: string;
  target: string;
  label?: string | undefined;
  emphasis?: "primary" | "neutral" | undefined;
}

export type Graph3DLayout =
  | "force"        // default — d3-force ball
  | "td"           // top-down DAG
  | "bu"           // bottom-up DAG
  | "lr"           // left-right DAG
  | "rl"           // right-left DAG
  | "radialout"    // radial — root in centre
  | "radialin"     // radial — leaves in centre
  | "zout";        // depth-outward (Z axis)

interface Graph3DProps {
  nodes: readonly Graph3DNode[];
  links: readonly Graph3DLink[];
  height?: number;
  /** Per-kind colour overrides. */
  palette?: Record<string, string>;
  /** Fires when the user clicks a 3D sphere — host shows a floater. */
  onNodeClick?: ((node: Graph3DNode) => void) | undefined;
  /** Layout algorithm — defaults to "force". */
  layout?: Graph3DLayout;
  /** When set, only the focused node + its 1-hop neighbours render at
   *  full opacity — everything else dims so the scene is legible. */
  focusedId?: string | null;
}

/* Single source of truth — pulls from NODE_PALETTE in
 * types/ontology.ts so the 3D palette matches the 2D palette exactly.
 * Same hex codes; same kind names. */
const ONTOLOGY_PALETTE: Record<string, string> = {
  // Real NodeKinds from types/ontology.ts NODE_PALETTE (TitleCase + lowercase)
  Project:    "#0a0a0a", project:    "#0a0a0a",
  Client:     "#3D6B9D", client:     "#3D6B9D",
  Intent:     "#7A3E5F", intent:     "#7A3E5F",
  Style:      "#6B5B4D", style:      "#6B5B4D",
  Room:       "#1a1a1a", room:       "#1a1a1a",
  Zone:       "#4a4a4a", zone:       "#4a4a4a",
  Placement:  "#3F7A55", placement:  "#3F7A55",
  SKU:        "#C95C1A", sku:        "#C95C1A",
  Material:   "#8B7968", material:   "#8B7968",
  Vendor:     "#3D6B9D", vendor:     "#3D6B9D",
  BudgetLine: "#3F7A55", budgetline: "#3F7A55",
  Milestone:  "#A04612", milestone:  "#A04612",
  Approval:   "#7A3E5F", approval:   "#7A3E5F",
  Rule:       "#0a0a0a", rule:       "#0a0a0a",
  Alert:      "#C95C1A", alert:      "#C95C1A",
  MemoryCard: "#6B5B4D", memorycard: "#6B5B4D",
};

const DEFAULT_PALETTE: Record<string, string> = {
  ...ONTOLOGY_PALETTE,
  // Agentic mesh kinds
  external:    "#C95C1A",
  protocol:    "#7A3E5F",
  supervisor:  "#141612",
  specialist:  "#3F7A55",
  store:       "#3D6B9D",
  // Ontology-map super-kinds (from OntologyMap)
  place:       "#3D6B9D",
  thing:       "#3F7A55",
  actor:       "#7A3E5F",
  memory:      "#D6A85C",
  money:       "#3F7A55",
  time:        "#D27ABA",
  // Flow-step kinds
  trigger:     "#C95C1A",
  automation:  "#7A766F",
  agent:       "#7A3E5F",
  moodboard:   "#D27ABA",
  catalog:     "#3F7A55",
  out:         "#7A766F",
};

/* ─── inner — renders react-force-graph-3d once the chunk is loaded ─── */

function Inner({ nodes, links, palette, onNodeClick, layout, focusedId }: { nodes: readonly Graph3DNode[]; links: readonly Graph3DLink[]; palette: Record<string, string>; onNodeClick?: ((node: Graph3DNode) => void) | undefined; layout: Graph3DLayout; focusedId: string | null }): JSX.Element {
  // 1-hop neighbourhood of the focused node — used to decide which
  // nodes stay bright + show labels, which links get particles, and
  // which non-neighbours dim (but stay visible — never hide).
  const neighbourhood = useMemo<Set<string> | null>(() => {
    if (!focusedId) return null;
    const set = new Set<string>([focusedId]);
    for (const l of links) {
      const s = typeof l.source === "string" ? l.source : (l.source as unknown as { id: string }).id;
      const t = typeof l.target === "string" ? l.target : (l.target as unknown as { id: string }).id;
      if (s === focusedId) set.add(t);
      if (t === focusedId) set.add(s);
    }
    return set;
  }, [focusedId, links]);
  // We avoid importing three at module-top so the 2D path stays lean —
  // grab the SpriteText helper inside Suspense.
  const fgRef = useRef<unknown>(null);
  const hasAutoFitRef = useRef<boolean>(false);
  // Hard camera reset — restores both position AND orientation so the
  // graph is guaranteed back on screen even after the user has orbited
  // far off-axis. `zoomToFit` alone only adjusts distance.
  const handleFit = useCallback(() => {
    type FG = {
      cameraPosition?: (p: { x: number; y: number; z: number }, lookAt?: { x: number; y: number; z: number }, ms?: number) => void;
      zoomToFit?: (ms?: number, padding?: number) => void;
    };
    const fg = fgRef.current as FG | null;
    try {
      fg?.cameraPosition?.({ x: 120, y: 80, z: 340 }, { x: 0, y: 0, z: 0 }, 600);
      // Let the camera arrive, then let zoomToFit dial in distance so
      // every node sits inside the viewport.
      setTimeout(() => { try { fg?.zoomToFit?.(700, 80); } catch { /* ignore */ } }, 650);
    } catch { /* ignore */ }
  }, []);
  // Re-fit when the user switches projects (data identity changes) so
  // the new graph isn't off-canvas on first paint.
  useEffect(() => {
    hasAutoFitRef.current = false;
  }, [nodes, links]);
  // Per-node three.js refs so we can re-style on focus change without
  // tearing down the scene (which would jolt the force layout). Built
  // inside `nodeThreeObject` (which is cached per node by force-graph),
  // mutated by the focus effect below.
  type NodeRefs = {
    mesh: import("three").Mesh;
    iconSprite: import("three").Sprite | null;
    labelSprite: { visible: boolean };
  };
  const nodeRefs = useRef<Map<string, NodeRefs>>(new Map());
  // Reset the refs map whenever the source graph identity changes
  // (e.g. user switches projects) so we don't hold stale entries.
  useEffect(() => {
    nodeRefs.current = new Map();
  }, [nodes, links]);
  // Apply focus state to the cached three objects — opacity + label
  // visibility, no scene rebuild.
  useEffect(() => {
    for (const [id, refs] of nodeRefs.current) {
      const inHood = focusedId == null ? false : (neighbourhood?.has(id) ?? false);
      const dim = focusedId != null && !inHood;
      const mat = refs.mesh.material as import("three").MeshStandardMaterial;
      mat.transparent = true;
      mat.opacity = dim ? 0.18 : 1;
      mat.emissiveIntensity = dim ? 0.02 : (inHood ? 0.45 : 0.12);
      if (refs.iconSprite) {
        const iconMat = refs.iconSprite.material as import("three").SpriteMaterial;
        iconMat.opacity = dim ? 0.18 : 1;
      }
      // Labels only on click: focused node + its 1-hop neighbours.
      refs.labelSprite.visible = inHood;
    }
  }, [focusedId, neighbourhood]);
  // Stable graph data shape required by react-force-graph-3d.
  const data = useMemo(() => ({
    nodes: nodes.map((n) => ({
      id: n.id,
      label: n.label,
      kind: n.kind,
      category: n.category ?? "",
      group: n.group ?? n.kind,
      color: n.brand_color ?? palette[n.kind] ?? "#7A766F",
      val: 6 + (n.kind === "supervisor" ? 6 : n.kind === "external" ? 2 : 0),
    })),
    links: links.map((l) => ({
      source: l.source,
      target: l.target,
      label: l.label ?? "",
      color: l.emphasis === "primary" ? "#C95C1A" : "#9a948b",
    })),
  }), [nodes, links, palette]);

  // Auto-rotate camera once on mount so the scene reads as 3D
  // immediately — Apple-correct subtle motion.
  useEffect(() => {
    const t = setTimeout(() => {
      const fg = fgRef.current as { cameraPosition?: (p: { x: number; y: number; z: number }, lookAt?: { x: number; y: number; z: number }, ms?: number) => void } | null;
      try { fg?.cameraPosition?.({ x: 120, y: 80, z: 340 }, { x: 0, y: 0, z: 0 }, 1200); } catch { /* ignore */ }
    }, 200);
    return () => clearTimeout(t);
  }, []);

  // ForceGraph3D types are generic; we use loose `any`-like shape for
  // the ref callback only — every payload is still our typed data.
  return (
    <div className="relative w-full h-full">
    <button
      type="button"
      onClick={handleFit}
      title="Reset camera + fit graph"
      className="absolute top-3 right-3 z-10 inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-cream/95 border border-border text-[11px] font-medium text-ink-700 hover:text-ink-950 backdrop-blur-sm shadow-sm transition"
    >
      <Maximize2 className="w-3 h-3" /> Recenter
    </button>
    <div className="absolute bottom-3 left-3 z-10 px-2 py-1 rounded-md bg-cream/85 border border-border/60 text-[10px] text-ink-500 backdrop-blur-sm pointer-events-none">
      drag · rotate &nbsp;·&nbsp; right-click drag · pan &nbsp;·&nbsp; scroll · zoom
    </div>
    <LazyForceGraph
      ref={fgRef as never}
      graphData={data}
      backgroundColor="#fefdfb"
      {...(layout !== "force" ? { dagMode: layout } : {})}
      dagLevelDistance={70}
      nodeColor={(n: unknown) => (n as { color?: string }).color ?? "#7A766F"}
      nodeOpacity={1}
      nodeResolution={24}
      nodeRelSize={5}
      linkOpacity={1}
      linkWidth={(l: unknown) => {
        if (!neighbourhood) return 1.1;
        const s = (l as { source: { id?: string } | string }).source;
        const t = (l as { target: { id?: string } | string }).target;
        const sid = typeof s === "string" ? s : s?.id;
        const tid = typeof t === "string" ? t : t?.id;
        const incident = Boolean(sid && tid && neighbourhood.has(sid) && neighbourhood.has(tid));
        return incident ? 2.4 : 0.5;
      }}
      linkDirectionalArrowLength={(l: unknown) => {
        if (!neighbourhood) return 0;
        const s = (l as { source: { id?: string } | string }).source;
        const t = (l as { target: { id?: string } | string }).target;
        const sid = typeof s === "string" ? s : s?.id;
        const tid = typeof t === "string" ? t : t?.id;
        return sid && tid && neighbourhood.has(sid) && neighbourhood.has(tid) ? 4 : 0;
      }}
      linkDirectionalArrowRelPos={1}
      linkDirectionalParticles={(l: unknown) => {
        if (!neighbourhood) return 0;
        const s = (l as { source: { id?: string } | string }).source;
        const t = (l as { target: { id?: string } | string }).target;
        const sid = typeof s === "string" ? s : s?.id;
        const tid = typeof t === "string" ? t : t?.id;
        return sid && tid && neighbourhood.has(sid) && neighbourhood.has(tid) ? 3 : 0;
      }}
      linkDirectionalParticleWidth={1.8}
      linkDirectionalParticleSpeed={0.014}
      linkColor={(l: unknown) => {
        const base = (l as { color?: string }).color ?? "#9a948b";
        if (!neighbourhood) return base;
        const s = (l as { source: { id?: string } | string }).source;
        const t = (l as { target: { id?: string } | string }).target;
        const sid = typeof s === "string" ? s : s?.id;
        const tid = typeof t === "string" ? t : t?.id;
        const incident = Boolean(sid && tid && neighbourhood.has(sid) && neighbourhood.has(tid));
        return incident ? "#C95C1A" : "#ece7dc";
      }}
      nodeLabel={(n: unknown): string => {
        const x = n as { label?: string; kind?: string };
        return `<div style="background:#fff;border:1px solid #e0dccf;border-radius:10px;padding:6px 10px;font-family:-apple-system,SF Pro Text,sans-serif;font-size:12px;color:#141612;box-shadow:0 4px 14px -2px rgba(0,0,0,0.12)"><strong>${x.label ?? ""}</strong><div style="color:#7a766f;font-size:11px;margin-top:2px">${x.kind ?? ""}</div></div>`;
      }}
      enableNodeDrag
      enableNavigationControls
      showNavInfo={false}
      controlType="trackball"
      cooldownTime={3500}
      onEngineStop={() => {
        if (!hasAutoFitRef.current) {
          hasAutoFitRef.current = true;
          handleFit();
        }
      }}
      onNodeClick={(n: unknown) => {
        if (!onNodeClick) return;
        const x = n as Graph3DNode;
        onNodeClick(x);
      }}
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      nodeThreeObject={((n: unknown) => {
        if (!SpriteTextCtor || !THREE_REF) {
          return THREE_REF ? new THREE_REF.Group() : (undefined as never);
        }
        const THREE = THREE_REF;
        const x = n as { label?: string; color?: string; kind?: string; category?: string };

        // ─── SOTA badge approach: pre-loaded canvas-texture sprite ───
        // One Sprite per node, texture = CanvasTexture (always valid).
        // The badge canvas is composed asynchronously: SVG → Image →
        // onload → fillRect(disk) + drawImage(icon) → CanvasTexture.
        // Three.js then has a fully-formed texture from the first
        // frame the sprite is added to the scene. No WebGL race.
        const IconCmp = iconForGraphNode(x.kind ?? "thing", x.category ?? "");
        const fillHex = x.color ?? "#7A766F";
        const k = (x.kind ?? "").toLowerCase();
        const baseScale = k === "project" ? 16 : k === "room" ? 13 : k === "supervisor" ? 17 : 11;

        const iconKey = (IconCmp as { displayName?: string; name?: string }).displayName
                     ?? (IconCmp as { name?: string }).name
                     ?? "fallback";
        const rawIcon = renderToStaticMarkup(
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          createElement(IconCmp as any, {
            size: 256,
            stroke: 3.0,
            color: "#fefdfb",
          }),
        );
        const iconSvg = /xmlns=/.test(rawIcon)
          ? rawIcon
          : rawIcon.replace("<svg ", '<svg xmlns="http://www.w3.org/2000/svg" ');

        // Empty group returned synchronously. Sprite is added once the
        // CanvasTexture is ready — three.js picks it up next frame.
        const group = new THREE.Group();
        const badgeMat = new THREE.SpriteMaterial({ transparent: true, depthWrite: false, opacity: 1 });
        const badge = new THREE.Sprite(badgeMat);
        badge.scale.set(baseScale, baseScale, 1);
        badge.visible = false; // until texture lands
        group.add(badge);

        loadBadgeTexture(THREE, fillHex, iconKey, iconSvg).then((tex) => {
          badgeMat.map = tex;
          badgeMat.needsUpdate = true;
          badge.visible = true;
        }).catch((err) => {
          // Surface so we see it on the SSE channel (CLAUDE.md §5).
          // eslint-disable-next-line no-console
          console.error("[Graph3D] badge texture load failed", err);
        });

        // Placeholder mesh for the focus-effect type contract (it
        // dims material.opacity on click-focus; the `badge` sprite
        // is what actually renders).
        const mesh = new THREE.Mesh(
          new THREE.PlaneGeometry(0.01, 0.01),
          new THREE.MeshBasicMaterial({ transparent: true, opacity: 0 }),
        );

        // Floating label sprite — cream pill, ink-950 text, soft border.
        // Hidden by default; the focus effect toggles visibility so
        // labels only appear for the clicked node + its neighbours.
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const sprite = new SpriteTextCtor(x.label ?? "") as any;
        sprite.color = "#141612";
        sprite.backgroundColor = "rgba(254,253,251,0.95)";
        sprite.borderColor = "rgba(20,22,18,0.12)";
        sprite.borderWidth = 0.25;
        sprite.borderRadius = 6;
        sprite.padding = 3;
        sprite.fontFace = '-apple-system, "SF Pro Text", "Inter", sans-serif';
        sprite.fontWeight = "600";
        sprite.textHeight = 5.5;
        sprite.position.y = 12;
        sprite.visible = false;

        group.add(sprite);
        const idStr = (n as { id: string }).id;
        nodeRefs.current.set(idStr, { mesh, iconSprite: badge, labelSprite: sprite });
        return group;
      }) as never}
    />
    </div>
  );
}

export function Graph3D({ nodes, links, height = 560, palette = {}, onNodeClick, layout = "force", focusedId = null }: Graph3DProps): JSX.Element {
  const merged = useMemo(() => ({ ...DEFAULT_PALETTE, ...palette }), [palette]);
  return (
    <div
      className="rounded-2xl border border-border/60 overflow-hidden bg-cream"
      style={{ height }}
    >
      <Suspense fallback={
        <div className="w-full h-full grid place-items-center text-ink-500 text-[12px]">
          <div className="inline-flex items-center gap-2">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading 3D viewer…
          </div>
        </div>
      }>
        <Inner nodes={nodes} links={links} palette={merged} onNodeClick={onNodeClick} layout={layout} focusedId={focusedId} />
      </Suspense>
    </div>
  );
}

export const LAYOUT_OPTIONS: ReadonlyArray<{ id: Graph3DLayout; label: string }> = [
  { id: "force",     label: "Force ball" },
  { id: "td",        label: "Tree · down" },
  { id: "bu",        label: "Tree · up" },
  { id: "lr",        label: "Tree · left → right" },
  { id: "rl",        label: "Tree · right → left" },
  { id: "radialout", label: "Radial · out" },
  { id: "radialin",  label: "Radial · in" },
  { id: "zout",      label: "Depth · Z" },
];
